import { Page } from '@app/components/page/Page';
import { scrollToTop } from '@app/helpers/scroll';
import { Paginated } from '@mi-front/core';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Box, Chip } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MRT_SortingState } from 'material-react-table';
import { ComponentType, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { queryClient } from '../integrations/react-query/react-query';
import { ListViewContext } from './context/ListViewContext';
import { DataToolbar } from './DataToolbar';
import { useListViewLogic, UseListViewLogicProps } from './hooks/useListViewLogic';
import { ListEmptyState } from './ListEmptyState';
import { ListViewLoadMore } from './ListViewLoadMore';
import { SortingChangeHandler } from './ListViewPaged';
import { ListViewQueryParams } from './types';

export interface ListViewInfiniteChildProps<TData> {
  data: TData[];
  total: number;
  hasMore: boolean;
  /** True when all results from the API have been loaded (end of list). */
  noMoreFromApi: boolean;
  /** True when load-more limit was reached but the API may still have more results. */
  loadMoreLimitReached: boolean;
  sorting: MRT_SortingState;
  onSortingChange: SortingChangeHandler;
  isLoading?: boolean;
  isFetching?: boolean;
}

export interface ListViewInfiniteProps<TData> extends UseListViewLogicProps {
  queryFn: (params: ListViewQueryParams) => Promise<Paginated<TData> | ArrayBuffer>;
  ListComponent: ComponentType<ListViewInfiniteChildProps<TData>>;
  actions?: React.ReactNode[];
  showProgress?: boolean;
  maxPages?: number;
  ListWrapper?: ComponentType<{ children: React.ReactNode }>;
}

/**
 * Infinite scrolling version of ListView.
 * Uses useInfiniteQuery and renders a Load More enabled list.
 */
export function ListViewInfinite<TData>({
  filters,
  listId,
  queryFn,
  exportFn,
  ListComponent,
  actions = [],
  defaultPageSize = 50,
  showProgress = false,
  maxPages = 3,
  ListWrapper = Page.Content,
}: ListViewInfiniteProps<TData>) {
  const { allParams, contextValue, onExport, handleSortingChange, sorting } = useListViewLogic({
    listId,
    filters,
    defaultPageSize,
    exportFn,
  });

  const { pageIndex, pageSize, sort, ...urlFilters } = allParams;

  /** Query key must match useInfiniteQuery so refresh invalidates the correct cache. */
  const infiniteQueryKey = useMemo(
    () => [listId, urlFilters, sort] as const,
    [listId, urlFilters, sort],
  );

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: infiniteQueryKey });
  }, [infiniteQueryKey]);

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: infiniteQueryKey,
      initialPageParam: 0,
      queryFn: async ({ pageParam = 0 }) => {
        const result = await queryFn({
          pageIndex: pageParam,
          pageSize,
          sort,
          filters: urlFilters,
        });
        return result as Paginated<TData>;
      },
      getNextPageParam: (lastPage, allPages) => {
        if (allPages.length >= maxPages) {
          return undefined;
        }

        const loadedCount = allPages.flatMap((p) => p.results).length;
        if (loadedCount >= lastPage.metadata.totalCount) {
          return undefined;
        }

        return allPages.length;
      },
    });

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.metadata.totalCount || 0;
  const pageCount = data?.pages.length ?? 0;
  const loadedCount = flatData.length;

  const noMoreFromApi = Boolean(!hasNextPage && data?.pages && loadedCount >= totalCount);
  const loadMoreLimitReached = Boolean(
    !hasNextPage && data?.pages != null && pageCount >= maxPages && loadedCount < totalCount,
  );

  const { $t } = useIntl();

  const hasNoResults = !isLoading && flatData.length === 0;

  return (
    <ListViewContext.Provider value={contextValue}>
      <DataToolbar
        actions={actions}
        onExport={onExport}
        onRefresh={onRefresh}
        total={totalCount}
        isLoading={isFetching}
        showProgress={showProgress}
      />
      <ListWrapper>
        {hasNoResults ? (
          <ListEmptyState />
        ) : (
          <>
            <ListComponent
              data={flatData}
              total={totalCount}
              hasMore={!!hasNextPage}
              noMoreFromApi={noMoreFromApi}
              loadMoreLimitReached={loadMoreLimitReached}
              isLoading={isLoading}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              isFetching={isFetching}
            />
            {hasNextPage && !isLoading && (
              <ListViewLoadMore
                onLoadMore={() => fetchNextPage()}
                isLoadingMore={isFetchingNextPage}
              />
            )}
            {loadMoreLimitReached && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Chip
                  icon={<ExpandLessIcon />}
                  label={$t({
                    id: 'common.listView.useFiltersToFindMore',
                    defaultMessage: 'Use los filtros para encontrar más resultados',
                  })}
                  onClick={scrollToTop}
                />
              </Box>
            )}
            {noMoreFromApi && loadedCount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Chip
                  disabled
                  label={$t({
                    id: 'common.listView.noMoreResults',
                    defaultMessage: 'No hay más resultados',
                  })}
                />
              </Box>
            )}
          </>
        )}
      </ListWrapper>
    </ListViewContext.Provider>
  );
}
